import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Star, FileDown, RotateCw } from "lucide-react";
import { api, API } from "../lib/api";
import BookingQRCard from "../components/BookingQRCard";
import ReferralCard from "../components/ReferralCard";
import { trackPurchase } from "../lib/tracking";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paymentIntentId = params.get("payment_intent");
  const bookingId = params.get("booking_id");
  const via = params.get("via"); // "embedded" if from new on-page flow
  const [state, setState] = React.useState({ status: "loading", payment_status: null, booking: null });
  const trackedRef = React.useRef(false);

  // Fire GA4/Pixel purchase event once when payment confirmed
  React.useEffect(() => {
    if (trackedRef.current) return;
    if (state.status === "success" && state.booking) {
      trackedRef.current = true;
      trackPurchase({
        booking_number: state.booking.booking_number,
        price_dkk: state.booking.price_dkk,
        delivery_type: state.booking.delivery_type,
      });
    }
  }, [state]);

  React.useEffect(() => {
    let attempts = 0;
    const maxAttempts = 8;
    let cancelled = false;

    const fetchBooking = async () => {
      if (!bookingId) return null;
      try { const r = await api.get(`/bookings/${bookingId}`); return r.data; } catch (e) { console.error("Booking fetch failed:", e); return null; }
    };

    const pollEmbedded = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/payments/intent-status/${paymentIntentId}`);
        if (data.payment_status === "paid" || data.status === "succeeded") {
          const bk = await fetchBooking();
          setState({ status: "success", payment_status: "paid", booking: bk });
          return;
        }
        if (attempts >= maxAttempts) { setState({ status: "timeout", payment_status: data.payment_status }); return; }
        setTimeout(pollEmbedded, 2000);
      } catch (e) {
        if (attempts >= maxAttempts) { setState({ status: "error" }); return; }
        setTimeout(pollEmbedded, 2000);
      }
    };

    const pollHosted = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const bk = await fetchBooking();
          setState({ status: "success", payment_status: "paid", booking: bk });
          return;
        }
        if (data.status === "expired") { setState({ status: "expired", payment_status: data.payment_status }); return; }
        if (attempts >= maxAttempts) { setState({ status: "timeout", payment_status: data.payment_status }); return; }
        setTimeout(pollHosted, 2000);
      } catch (e) {
        console.error("Checkout status poll failed:", e);
        if (attempts >= maxAttempts) { setState({ status: "error" }); return; }
        setTimeout(pollHosted, 2000);
      }
    };

    if (paymentIntentId) {
      pollEmbedded();
    } else if (sessionId) {
      pollHosted();
    } else if (params.get("already_paid") === "1" && bookingId) {
      // Already-paid shortcut from EmbeddedCheckoutPage
      (async () => {
        const bk = await fetchBooking();
        setState({ status: "success", payment_status: "paid", booking: bk });
      })();
    } else {
      setState({ status: "error", payment_status: "unknown" });
    }
    return () => { cancelled = true; };
  }, [sessionId, paymentIntentId, bookingId, via, params]);

  return (
    <div className="bg-brand-bg2 min-h-[80vh]" data-testid="payment-success-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        {state.status === "loading" && (
          <div className="bg-white border border-brand p-10 text-center" data-testid="payment-loading">
            <Loader2 className="animate-spin text-brand-red mx-auto" size={40} />
            <h1 className="font-display font-semibold text-2xl mt-5 text-brand-dark">Bekræfter betaling...</h1>
            <p className="text-brand-text2 mt-2">Vent et øjeblik — vi tjekker din betaling hos Stripe.</p>
          </div>
        )}
        {state.status === "success" && (
          <div className="bg-white border border-brand p-10" data-testid="payment-success">
            <div className="w-14 h-14 bg-brand-red text-white flex items-center justify-center"><CheckCircle2 size={28} /></div>
            <div className="kicker mt-6">Bekræftelse</div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-brand-dark mt-3">Tak! Din booking er bekræftet.</h1>
            <p className="text-brand-text2 mt-4 leading-relaxed">
              Vi har modtaget din betaling og din afhentning er booket. Din faktura er sendt til <strong className="text-brand-dark">{state.booking?.customer_email}</strong> — tjek også din spam-mappe.
            </p>
            {state.booking && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 border-t border-brand pt-6 mt-7 text-sm">
                <div><div className="text-xs uppercase tracking-widest text-brand-text2">Booking</div><div className="font-display font-semibold text-brand-dark mt-1">{state.booking.booking_number}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-brand-text2">Beløb</div><div className="font-display font-semibold text-brand-dark mt-1">{state.booking.total_amount_dkk} kr <span className="text-[10px] text-brand-text2 font-normal">inkl. moms</span></div></div>
                <div><div className="text-xs uppercase tracking-widest text-brand-text2">Afhentning</div><div className="font-display font-semibold text-brand-dark mt-1">{state.booking.pickup_time}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-brand-text2">Pakker</div><div className="font-display font-semibold text-brand-dark mt-1">{state.booking.package_count}</div></div>
              </div>
            )}

            {/* Customer-facing QR code shown right after payment */}
            {state.booking?.qr_token && (
              <div className="mt-8">
                <BookingQRCard
                  qrToken={state.booking.qr_token}
                  bookingNumber={state.booking.booking_number}
                  status={state.booking.booking_status}
                />
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-8">
              {state.booking && (
                <>
                  <Link
                    to={`/track?b=${encodeURIComponent(state.booking.booking_number)}&e=${encodeURIComponent(state.booking.customer_email)}`}
                    data-testid="success-cta-track"
                    className="btn-primary"
                  >
                    Spor din pakke <ArrowRight size={16} />
                  </Link>
                  <a
                    href={`${API}/invoice/${state.booking.id}?email=${encodeURIComponent(state.booking.customer_email)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="success-cta-invoice"
                    className="btn-secondary"
                  >
                    <FileDown size={16} /> Download faktura (PDF)
                  </a>
                  <Link
                    to={`/book?reorder=${encodeURIComponent(state.booking.booking_number)}&e=${encodeURIComponent(state.booking.customer_email)}`}
                    data-testid="success-cta-reorder"
                    className="btn-secondary"
                  >
                    <RotateCw size={16} /> Genbestil
                  </Link>
                </>
              )}
              <Link to="/" data-testid="success-cta-home" className="btn-secondary">Til forsiden</Link>
            </div>

            {/* Review CTA */}
            {state.booking && (
              <div className="border-t border-brand mt-10 pt-8" data-testid="review-cta-block">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#00B67A] text-white flex items-center justify-center flex-shrink-0">
                    <Star size={22} fill="white" strokeWidth={0} />
                  </div>
                  <div className="flex-1">
                    <div className="kicker">Bedøm os bagefter</div>
                    <h3 className="font-display font-semibold text-xl text-brand-dark mt-2">Når din pakke er leveret — del din oplevelse</h3>
                    <p className="text-brand-text2 text-sm mt-2">Din feedback hjælper os med at blive bedre — og giver andre kunder tryghed til at vælge EMA Kurer. Det tager 30 sekunder.</p>
                    <Link
                      to={`/anmeldelser?b=${encodeURIComponent(state.booking.booking_number)}&e=${encodeURIComponent(state.booking.customer_email)}`}
                      className="btn-secondary mt-4 inline-flex"
                      data-testid="success-cta-review"
                    >
                      Skriv en anmeldelse <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {state.booking?.customer_email && (
              <div className="mt-6">
                <ReferralCard customerEmail={state.booking.customer_email} />
              </div>
            )}
          </div>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <div className="bg-white border border-brand p-10" data-testid="payment-error">
            <div className="w-14 h-14 bg-amber-500 text-white flex items-center justify-center"><XCircle size={28} /></div>
            <h1 className="font-display font-bold text-3xl text-brand-dark mt-6">Vi kunne ikke bekræfte betaling.</h1>
            <p className="text-brand-text2 mt-3">Hvis pengene er trukket, modtager du en mail inden for kort tid. Ellers prøv igen — eller kontakt support.</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/book" className="btn-primary" data-testid="error-cta-retry">Prøv igen</Link>
              <a href="mailto:kontakt@emakurer.dk" className="btn-secondary" data-testid="error-cta-mail">Kontakt support</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
